import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, PriceInput, Select, Textarea } from '@artosera/ui';
import type { ContratLocationCreation } from '@artosera/shared';
import { useContact, type ContactPresente } from '../../api/contacts.js';
import { useEditionsOeuvre, type OeuvrePresentee } from '../../api/oeuvres.js';
import type { ContratLocationPresente } from '../../api/locations.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import { Field } from '../Field.js';
import styles from './Locations.module.css';

type Option = ContratLocationCreation['optionAchat'];
type Ligne = ContratLocationCreation['lignes'][number] & { titre: string };
export function jourCourantLocation() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Paris' }).format(new Date());
}
export function OptionForm({
  valeur,
  changer,
}: {
  valeur: Option;
  changer: (option: Option) => void;
}) {
  const { t, i18n } = useTranslation('regie');
  const identifiant = useId();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (
    <fieldset className={styles.form}>
      <legend>{t('locations.option')}</legend>
      <label>
        <input
          type="checkbox"
          checked={valeur !== null}
          onChange={(e) =>
            changer(
              e.target.checked
                ? {
                    prixConvenuCentimes: 0,
                    pourcentagePointsDeBase: 0,
                    plafondCentimes: 0,
                    valableJusquAu: jourCourantLocation(),
                  }
                : null,
            )
          }
        />
        {t('locations.optionProposee')}
      </label>
      {valeur && (
        <div className={styles.champs}>
          <Field label={t('locations.prixConvenu')} htmlFor={`${identifiant}-prix`}>
            <PriceInput
              id={`${identifiant}-prix`}
              lang={langue}
              cents={valeur.prixConvenuCentimes}
              onCentsChange={(v) => changer({ ...valeur, prixConvenuCentimes: v })}
            />
          </Field>
          <Field label={t('locations.pourcentage')} htmlFor={`${identifiant}-pct`}>
            <Input
              id={`${identifiant}-pct`}
              type="number"
              min={0}
              max={100}
              value={valeur.pourcentagePointsDeBase / 100}
              onChange={(e) =>
                changer({ ...valeur, pourcentagePointsDeBase: Number(e.target.value) * 100 })
              }
            />
          </Field>
          <Field label={t('locations.plafond')} htmlFor={`${identifiant}-plafond`}>
            <PriceInput
              id={`${identifiant}-plafond`}
              lang={langue}
              cents={valeur.plafondCentimes}
              onCentsChange={(v) => changer({ ...valeur, plafondCentimes: v })}
            />
          </Field>
          <Field label={t('locations.validite')} htmlFor={`${identifiant}-date`}>
            <Input
              id={`${identifiant}-date`}
              type="date"
              value={valeur.valableJusquAu}
              onChange={(e) => changer({ ...valeur, valableJusquAu: e.target.value })}
            />
          </Field>
        </div>
      )}
    </fieldset>
  );
}
export function ChoixLigneLocation({
  galerieSlug,
  onAjouter,
}: {
  galerieSlug: string;
  onAjouter: (ligne: Ligne) => void;
}) {
  const { t } = useTranslation('regie');
  const [oeuvre, setOeuvre] = useState<OeuvrePresentee | null>(null);
  const [exemplaireId, setExemplaire] = useState('');
  const editions = useEditionsOeuvre(galerieSlug, oeuvre?.type === 'edition' ? oeuvre.id : null);
  const [erreur, setErreur] = useState('');
  function ajouter(o: OeuvrePresentee, exemplaire: string | null) {
    onAjouter({
      oeuvreId: o.id,
      exemplaireId: exemplaire,
      titre: o.titre,
      loyerMensuelHtCentimes: 0,
      valeurReferenceCentimes: o.prixPublicCentimes ?? 0,
      optionAchat: null,
    });
    setOeuvre(null);
    setExemplaire('');
  }
  return (
    <div className={styles.form}>
      <RechercheOeuvre
        galerieSlug={galerieSlug}
        onChoisir={(o) => {
          setErreur('');
          if (o.type === 'edition') {
            setOeuvre(o);
            setExemplaire('');
            return;
          }
          if (o.statut !== 'disponible') {
            setErreur(t('locations.indisponible'));
            return;
          }
          ajouter(o, null);
        }}
      />
      {erreur && <p role="alert">{erreur}</p>}
      {oeuvre && (
        <>
          <p>{oeuvre.titre}</p>
          <Select
            aria-label={t('locations.exemplaire')}
            value={exemplaireId}
            onValueChange={setExemplaire}
            options={[
              { value: '', label: '—' },
              ...(editions.data?.editions ?? [])
                .filter((e) => e.statut === 'disponible')
                .map((e) => ({ value: e.id, label: e.numero })),
            ]}
          />
          <Button
            type="button"
            disabled={!exemplaireId}
            onClick={() => ajouter(oeuvre, exemplaireId)}
          >
            {t('locations.ajouter')}
          </Button>
          {editions.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        </>
      )}
    </div>
  );
}
export function ContratForm({
  galerieSlug,
  contrat,
  enCours,
  onSoumettre,
}: {
  galerieSlug: string;
  contrat?: ContratLocationPresente;
  enCours: boolean;
  onSoumettre: (corps: ContratLocationCreation) => Promise<void>;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const initial = useContact(galerieSlug, contrat?.locataireId ?? null);
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const [contactEfface, setContactEfface] = useState(false);
  const choisi = contact ?? (!contactEfface ? (initial.data?.contact ?? null) : null);
  const [lignes, setLignes] = useState<Ligne[]>(
    contrat?.lignes.map((l) => ({
      oeuvreId: l.oeuvreId,
      exemplaireId: l.exemplaireId,
      titre: l.titre,
      loyerMensuelHtCentimes: l.loyerMensuelHtCentimes,
      valeurReferenceCentimes: l.valeurReferenceCentimes,
      optionAchat: l.optionAchat,
    })) ?? [],
  );
  const [adresse, setAdresse] = useState(
    contrat?.adresseInstallation ?? { ligne1: '', codePostal: '', ville: '', pays: 'FR' },
  );
  const [debut, setDebut] = useState(contrat?.debutLe ?? jourCourantLocation());
  const [duree, setDuree] = useState(contrat?.dureeMois?.toString() ?? '');
  const [preavis, setPreavis] = useState(contrat?.preavisMois ?? 1);
  const [jour, setJour] = useState(contrat?.jourFacturation ?? 5);
  const [depot, setDepot] = useState(contrat?.depotGarantie.montantCentimes ?? 0);
  const [assurance, setAssurance] = useState<ContratLocationCreation['assurance']>(
    contrat?.assurance ?? { charge: 'locataire', assureur: '', police: '' },
  );
  const [rotation, setRotation] = useState(
    contrat?.conditionsRotation.periodiciteMois?.toString() ?? '',
  );
  const [conditions, setConditions] = useState(contrat?.conditionsRotation.texte ?? '');
  const [option, setOption] = useState<Option>(contrat?.optionAchat ?? null);
  const [notes, setNotes] = useState(contrat?.notes ?? '');
  const [erreur, setErreur] = useState('');
  const restreint = contrat !== undefined && contrat.etat !== 'brouillon';
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        setErreur('');
        if (!choisi || lignes.length === 0) {
          setErreur(t('locations.champsRequis'));
          return;
        }
        void onSoumettre({
          locataireId: choisi.id,
          adresseInstallation: adresse,
          debutLe: debut,
          dureeMois: duree ? Number(duree) : null,
          preavisMois: preavis,
          jourFacturation: jour,
          depotGarantieCentimes: depot,
          assurance,
          conditionsRotation: {
            periodiciteMois: rotation ? Number(rotation) : null,
            texte: conditions,
          },
          lignes: lignes.map(({ titre, ...ligne }) => {
            void titre;
            return ligne;
          }),
          optionAchat: option,
          notes,
        }).catch((e) => setErreur(e instanceof Error ? e.message : t('commun:erreurReseau')));
      }}
    >
      {!restreint && (
        <>
          <fieldset>
            <legend>{t('locations.locataire')}</legend>
            <RechercheContact
              galerieSlug={galerieSlug}
              contact={choisi}
              onChoisir={(c) => {
                setContact(c);
                setContactEfface(false);
              }}
              onEffacer={() => {
                setContact(null);
                setContactEfface(true);
              }}
            />
          </fieldset>
          <div className={styles.champs}>
            {[
              [
                'debut',
                <Input
                  type="date"
                  required
                  value={debut}
                  onChange={(e) => setDebut(e.target.value)}
                />,
              ],
              [
                'duree',
                <Input
                  type="number"
                  min={1}
                  value={duree}
                  onChange={(e) => setDuree(e.target.value)}
                />,
              ],
              [
                'preavis',
                <Input
                  type="number"
                  min={0}
                  value={preavis}
                  onChange={(e) => setPreavis(Number(e.target.value))}
                />,
              ],
              [
                'jourFacturation',
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={jour}
                  onChange={(e) => setJour(Number(e.target.value))}
                />,
              ],
              ['depot', <PriceInput lang={langue} cents={depot} onCentsChange={setDepot} />],
            ].map(([cle, champ]) => (
              <label key={String(cle)}>
                {t(`locations.${cle}`)}
                {champ}
              </label>
            ))}
          </div>
          <fieldset className={styles.form}>
            <legend>{t('locations.oeuvres')}</legend>
            {lignes.map((ligne, index) => (
              <section className={styles.carte} key={`${ligne.oeuvreId}:${ligne.exemplaireId}`}>
                <strong>{ligne.titre}</strong>
                <div className={styles.champs}>
                  <label>
                    {t('locations.loyer')}
                    <PriceInput
                      lang={langue}
                      cents={ligne.loyerMensuelHtCentimes}
                      onCentsChange={(v) =>
                        setLignes(
                          lignes.map((l, i) =>
                            i === index ? { ...l, loyerMensuelHtCentimes: v } : l,
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    {t('locations.valeur')}
                    <PriceInput
                      lang={langue}
                      cents={ligne.valeurReferenceCentimes ?? 0}
                      onCentsChange={(v) =>
                        setLignes(
                          lignes.map((l, i) =>
                            i === index ? { ...l, valeurReferenceCentimes: v } : l,
                          ),
                        )
                      }
                    />
                  </label>
                </div>
                <OptionForm
                  valeur={ligne.optionAchat}
                  changer={(o) =>
                    setLignes(lignes.map((l, i) => (i === index ? { ...l, optionAchat: o } : l)))
                  }
                />
                <Button
                  type="button"
                  onClick={() => setLignes(lignes.filter((_, i) => i !== index))}
                >
                  {t('locations.retirer')}
                </Button>
              </section>
            ))}
            <ChoixLigneLocation
              galerieSlug={galerieSlug}
              onAjouter={(l) => {
                if (
                  lignes.some((v) => v.oeuvreId === l.oeuvreId && v.exemplaireId === l.exemplaireId)
                ) {
                  setErreur(t('locations.dejaChoisie'));
                  return;
                }
                setLignes([...lignes, l]);
              }}
            />
          </fieldset>
          <OptionForm valeur={option} changer={setOption} />
        </>
      )}
      <fieldset className={styles.champs}>
        <legend>{t('locations.adresse')}</legend>
        {(['ligne1', 'ligne2', 'codePostal', 'ville', 'pays'] as const).map((cle) => (
          <label key={cle}>
            {t(`locations.adresseChamps.${cle}`)}
            <Input
              required={cle !== 'ligne2'}
              value={adresse[cle] ?? ''}
              onChange={(e) => setAdresse({ ...adresse, [cle]: e.target.value })}
            />
          </label>
        ))}
      </fieldset>
      <fieldset className={styles.champs}>
        <legend>{t('locations.assurance')}</legend>
        <Select
          aria-label={t('locations.chargeAssurance')}
          value={assurance.charge}
          onValueChange={(v) =>
            setAssurance({ ...assurance, charge: v as 'locataire' | 'galerie' })
          }
          options={['locataire', 'galerie'].map((v) => ({ value: v, label: t(`locations.${v}`) }))}
        />
        {(['assureur', 'police'] as const).map((cle) => (
          <label key={cle}>
            {t(`locations.${cle}`)}
            <Input
              value={assurance[cle] ?? ''}
              onChange={(e) => setAssurance({ ...assurance, [cle]: e.target.value })}
            />
          </label>
        ))}
      </fieldset>
      <label>
        {t('locations.rotationMois')}
        <Input
          type="number"
          min={1}
          value={rotation}
          onChange={(e) => setRotation(e.target.value)}
        />
      </label>
      <label>
        {t('locations.conditionsRotation')}
        <Textarea value={conditions} onChange={(e) => setConditions(e.target.value)} />
      </label>
      <label>
        {t('locations.notes')}
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      {erreur && <p role="alert">{erreur}</p>}
      <Button disabled={enCours || (restreint && !choisi)} type="submit">
        {t('locations.enregistrer')}
      </Button>
    </form>
  );
}
